import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { peso, num, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ClipboardCheck, Eye, ArrowLeft } from "lucide-react";

const varianceClass = (v) => {
  if (v === null) return "text-muted-foreground";
  if (v === 0) return "text-green-700";
  return "text-red-600";
};

const impactClass = (n) => {
  if (n < 0) return "text-red-600";
  if (n > 0) return "text-green-700";
  return "";
};

function CountSheet({ onDone }) {
  const { writableBranches, selected } = useBranch();
  const [branchId, setBranchId] = useState(
    writableBranches.some((b) => b.branch_id === selected) ? selected : writableBranches[0]?.branch_id || ""
  );
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    Promise.all([api.get("/items"), api.get("/stock", { params: { branch_id: branchId } })]).then(([ir, sr]) => {
      const stockMap = {};
      sr.data.forEach((s) => { stockMap[s.item_id] = s.qty; });
      setRows(ir.data.map((i) => ({
        item_id: i.item_id, item_name: i.name, unit: i.unit, category: i.category,
        system_qty: Math.round((stockMap[i.item_id] || 0) * 100) / 100, counted: "",
      })));
    });
  }, [branchId]);

  const setCounted = (item_id, v) => setRows(rows.map((r) => (r.item_id === item_id ? { ...r, counted: v } : r)));

  const counted = rows.filter((r) => r.counted !== "" && !isNaN(parseFloat(r.counted)));
  const variances = counted.filter((r) => parseFloat(r.counted) !== r.system_qty);

  const post = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/stock-counts", {
        branch_id: branchId,
        lines: counted.map((r) => ({ item_id: r.item_id, counted_qty: parseFloat(r.counted) })),
      });
      toast.success(`Count posted — ${data.adjustments} adjustment${data.adjustments === 1 ? "" : "s"} (${peso(data.variance_value)} impact)`);
      triggerRefresh();
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const filtered = rows.filter((r) => (r.item_name + r.category).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="count-sheet">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-2">
          <Label>Branch to count</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="w-64" data-testid="count-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>{writableBranches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Input placeholder="Search items…" className="w-48 sm:w-64" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="count-search-input" />
      </div>
      <p className="text-xs text-muted-foreground">Enter the physical count for each item you checked. Leave blank to skip — only entered items are compared and adjusted.</p>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Item</th><th className="py-2 px-3">Category</th>
              <th className="py-2 px-3 text-right">System qty</th><th className="py-2 px-3 text-right">Counted</th>
              <th className="py-2 px-3 text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const c = r.counted === "" ? null : parseFloat(r.counted);
              const v = c === null || isNaN(c) ? null : Math.round((c - r.system_qty) * 100) / 100;
              return (
                <tr key={r.item_id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="py-1.5 px-3 font-medium">{r.item_name}</td>
                  <td className="py-1.5 px-3 text-muted-foreground">{r.category}</td>
                  <td className="py-1.5 px-3 text-right">{num(r.system_qty)} {r.unit}</td>
                  <td className="py-1.5 px-3 text-right">
                    <Input type="number" step="any" min="0" className="w-24 h-9 ml-auto" value={r.counted}
                      onChange={(e) => setCounted(r.item_id, e.target.value)} data-testid={`count-input-${r.item_id}`} />
                  </td>
                  <td className={`py-1.5 px-3 text-right font-medium ${varianceClass(v)}`}>
                    {v === null ? "—" : `${v > 0 ? "+" : ""}${num(v)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sticky bottom-20 lg:bottom-4 border rounded-xl bg-white shadow-md p-4 flex flex-wrap items-center justify-between gap-3" data-testid="count-footer">
        <div className="text-sm">
          <span className="font-semibold">{counted.length}</span> items counted ·{" "}
          <span className={variances.length ? "text-red-600 font-medium" : "text-green-700"}>{variances.length} variance{variances.length === 1 ? "" : "s"}</span>
        </div>
        <Button onClick={post} disabled={loading || counted.length === 0} data-testid="post-count-btn">
          <ClipboardCheck className="h-4 w-4 mr-1" /> {loading ? "Posting…" : "Post adjustments"}
        </Button>
      </div>
    </div>
  );
}

export default function StockCounts() {
  const { auth } = useAuth();
  const canCount = ["owner", "manager"].includes(auth?.membership?.role);
  const [counts, setCounts] = useState([]);
  const [counting, setCounting] = useState(false);
  const [viewing, setViewing] = useState(null);

  useRefresh(() => api.get("/stock-counts").then((r) => setCounts(r.data)), []);

  if (counting) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setCounting(false)} data-testid="count-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Physical count</h1>
        </div>
        <CountSheet onDone={() => setCounting(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="stock-counts-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Stock counts</h1>
        {canCount && (
          <Button onClick={() => setCounting(true)} data-testid="start-count-btn">
            <ClipboardCheck className="h-4 w-4 mr-1" /> Start count
          </Button>
        )}
      </div>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">Date</th><th className="py-2 px-3">Branch</th><th className="py-2 px-3">Counted by</th>
              <th className="py-2 px-3 text-right">Items</th><th className="py-2 px-3 text-right">Adjustments</th>
              <th className="py-2 px-3 text-right">Value impact</th><th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {counts.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No counts yet — start your first physical count.</td></tr>}
            {counts.map((c) => (
              <tr key={c.count_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`count-row-${c.count_id}`}>
                <td className="py-2 px-3">{fmtDate(c.created_at)}</td>
                <td className="py-2 px-3">{c.branch_name}</td>
                <td className="py-2 px-3 text-muted-foreground">{c.counted_by}</td>
                <td className="py-2 px-3 text-right">{c.items_counted}</td>
                <td className="py-2 px-3 text-right">
                  <Badge className={c.adjustments ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-green-100 text-green-800 hover:bg-green-100"}>
                    {c.adjustments}
                  </Badge>
                </td>
                <td className={`py-2 px-3 text-right font-medium ${impactClass(c.variance_value)}`}>
                  {peso(c.variance_value)}
                </td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setViewing(c)} data-testid={`view-count-${c.count_id}`}><Eye className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="count-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>Count · {viewing.branch_name} · {fmtDate(viewing.created_at)}</DialogTitle>
              </DialogHeader>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                  <tr className="border-b">
                    <th className="py-2 pr-2">Item</th><th className="py-2 px-2 text-right">System</th>
                    <th className="py-2 px-2 text-right">Counted</th><th className="py-2 px-2 text-right">Variance</th>
                    <th className="py-2 pl-2 text-right">Value impact</th>
                  </tr>
                </thead>
                <tbody>
                  {viewing.lines.map((l) => (
                    <tr key={l.item_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 font-medium">{l.item_name}</td>
                      <td className="py-1.5 px-2 text-right">{num(l.system_qty)} {l.unit}</td>
                      <td className="py-1.5 px-2 text-right">{num(l.counted_qty)}</td>
                      <td className={`py-1.5 px-2 text-right font-medium ${l.variance === 0 ? "text-green-700" : "text-red-600"}`}>
                        {l.variance > 0 ? "+" : ""}{num(l.variance)}
                      </td>
                      <td className="py-1.5 pl-2 text-right">{peso(l.value_impact)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
